/**
 * MCP Server API Bridge
 *
 * Client-side API for MCP server config shaping.
 * Runtime MCP operations are disabled on the Nest path until Nest exposes a
 * generic MCP surface.
 */

import type { McpOAuthTokens, McpServerItem } from "../persistence/mcpServerStore"
import type { McpHttpServerConfig, McpServerConfig, McpStdioServerConfig } from "../providerEvents/harnessEventTypes"

// Re-export types for convenience
export type { McpServerConfig, McpStdioServerConfig, McpHttpServerConfig }

// ============================================================================
// Type Definitions
// ============================================================================

export interface TestMcpConnectionResponse {
    success: boolean
    error?: string
    requiresAuth?: boolean
    authDetails?: {
        resourceMetadataUrl?: string
        scope?: string
    }
}

export interface InitiateMcpOAuthParams {
    serverId: string
    serverUrl: string // MCP server URL - OAuth endpoints are discovered automatically
}

export interface InitiateMcpOAuthResponse {
    success: boolean
    error?: string
}

export interface CancelMcpOAuthParams {
    serverId: string
}

export interface CancelMcpOAuthResponse {
    success: boolean
}

export interface RefreshMcpOAuthParams {
    serverId: string
    serverUrl: string
    refreshToken: string
}

export interface RefreshMcpOAuthResponse {
    success: boolean
    tokens?: McpOAuthTokens
    error?: string
}

export type OnMcpOAuthCompleteCallback = (result: { serverId: string; tokens: McpOAuthTokens } | { serverId: string; error: string }) => void

// ============================================================================
export function isMcpApiAvailable(): boolean {
    return false
}

/**
 * Test connection to an MCP server
 * Returns connection status and available capabilities
 */
export async function testMcpConnection(config: McpServerConfig): Promise<TestMcpConnectionResponse> {
    void config
    return { success: false, error: "MCP runtime operations are not available on the Nest path yet." }
}

/**
 * Initiate OAuth flow for an MCP server
 * Opens OAuth URL in system browser and handles callback
 */
export async function initiateMcpOAuth(params: InitiateMcpOAuthParams): Promise<InitiateMcpOAuthResponse> {
    void params
    return { success: false, error: "MCP OAuth is not available on the Nest path yet." }
}

/**
 * Cancel a pending OAuth flow for an MCP server
 */
export async function cancelMcpOAuth(params: CancelMcpOAuthParams): Promise<CancelMcpOAuthResponse> {
    void params
    return { success: false }
}

/**
 * Refresh OAuth tokens for an MCP server using the refresh token
 */
export async function refreshMcpOAuthToken(params: RefreshMcpOAuthParams): Promise<RefreshMcpOAuthResponse> {
    void params
    return { success: false, error: "MCP token refresh is not available on the Nest path yet." }
}

/**
 * Subscribe to OAuth completion events
 * Returns an unsubscribe function
 */
export function onMcpOAuthComplete(callback: OnMcpOAuthCompleteCallback): () => void {
    void callback
    return () => {}
}

/**
 * Build MCP server configs from McpServerItem array.
 * Returns a Record keyed by server name for SDK compatibility.
 * Filters to only include enabled servers.
 */
export function buildMcpServerConfigs(servers: McpServerItem[]): Record<string, McpServerConfig> {
    const result: Record<string, McpServerConfig> = {}

    for (const server of servers) {
        if (!server.enabled) continue

        if (server.transportType === "http") {
            // Build headers including OAuth token if available
            const headers: Record<string, string> = { ...server.headers }
            if (server.oauthTokens?.accessToken) {
                headers.Authorization = `Bearer ${server.oauthTokens.accessToken}`
            }

            const config: McpHttpServerConfig = {
                type: "http",
                url: server.url,
            }
            if (Object.keys(headers).length > 0) {
                config.headers = headers
            }
            result[server.name] = config
        } else {
            // stdio server
            const config: McpStdioServerConfig = {
                type: "stdio",
                command: server.command,
                args: server.args ?? [],
            }
            if (server.envVars && Object.keys(server.envVars).length > 0) {
                config.env = server.envVars
            }
            result[server.name] = config
        }
    }

    return result
}
